import React, { useCallback } from "react";
import "./PublishStatusToggle.css";

export const PUBLISH_STATUS = {
  PUBLISHED: "published",
  DRAFT: "draft",
};

/**
 * Modern pill toggle for Published / Draft status.
 * @param {boolean} isPublished - true when status is Published (is_published = 1)
 * @param {(status: 'published' | 'draft') => void | Promise<void>} onChange
 * @param {boolean} [loading=false] - show saving state and block interaction
 * @param {boolean} [disabled=false] - disable toggle entirely
 * @param {string} [className] - optional wrapper class
 * @param {string} [id] - element id for accessibility
 */
const PublishStatusToggle = ({
  isPublished,
  onChange,
  loading = false,
  disabled = false,
  className = "",
  id = "publish-status-toggle",
}) => {
  const isDraft = !isPublished;
  const isInteractive = !disabled && !loading;

  const handleToggle = useCallback(() => {
    if (!isInteractive) return;
    const nextStatus = isPublished ? PUBLISH_STATUS.DRAFT : PUBLISH_STATUS.PUBLISHED;
    onChange?.(nextStatus);
  }, [isInteractive, isPublished, onChange]);

  const handleKeyDown = useCallback(
    (event) => {
      if (!isInteractive) return;
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleToggle();
      }
    },
    [handleToggle, isInteractive]
  );

  const statusLabel = isPublished ? "Published" : "Draft";

  return (
    <div className={`publish-status-toggle-wrap ${className}`.trim()}>
      <button
        type="button"
        id={id}
        role="switch"
        aria-checked={isPublished}
        aria-busy={loading}
        aria-label={`Appraisal setup status: ${statusLabel}. Click to change.`}
        disabled={!isInteractive}
        className={[
          "publish-status-toggle",
          isPublished ? "is-published" : "is-draft",
          loading ? "is-loading" : "",
          disabled ? "is-disabled" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
      >
        <span className="publish-status-toggle__track" aria-hidden="true">
          <span className="publish-status-toggle__thumb" />
          <span className="publish-status-toggle__label">{statusLabel}</span>
        </span>
        {loading && (
          <span className="publish-status-toggle__loader" aria-hidden="true">
            <span className="publish-status-toggle__spinner" />
          </span>
        )}
      </button>
      <span className="publish-status-toggle__hint" aria-live="polite">
        {loading ? "Saving…" : isDraft ? "Draft (No PAFs will be generated)" : "Published (PAFs will be generated)"}
      </span>
    </div>
  );
};

export default PublishStatusToggle;
